import { useCallback } from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import { FaTwitter, FaFacebookF, FaInstagram, FaLinkedin } from "react-icons/fa";
import Particles from "react-tsparticles";
import { loadSlim } from "tsparticles-slim";
import { motion } from "framer-motion";
import "./landing.css";
import Logo from "./assets/logo.png";
import { Link, useNavigate } from "react-router-dom";

const Footer = () => {
  const navigate = useNavigate();
  const particlesInit = useCallback(async (engine) => {
    await loadSlim(engine);
  }, []);

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <section>
      <footer
        style={{
          position: "relative",
          padding: "60px 0 20px",
          overflow: "hidden",
          background: "linear-gradient(90deg, #F6D5F7 0%, #fff 100%)",
          color: "#000",
        }}
      >
        {/* Background Particles */}
        <Particles
          id="footerParticles"
          init={particlesInit}
          options={{
            fullScreen: { enable: false },
            fpsLimit: 60,
            particles: {
              number: { value: 100, density: { enable: true, area: 800 } },
              size: { value: { min: 2, max: 5 } },
              move: { enable: true, speed: 0.6, outModes: "out" },
              opacity: { value: 0.25 },
              color: { value: ["#6211a3ff", "#b700ffff", "#e6f2f1ff"] },
              links: {
                enable: true,
                distance: 180,
                color: "#6125b5ff",
                opacity: 0.2,
                width: 1.5,
              },
            },
            interactivity: {
              events: { onHover: { enable: true, mode: "grab" } },
              modes: { grab: { distance: 200, links: { opacity: 0.4 } } },
            },
            detectRetina: true,
          }}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 0,
          }}
        />

        {/* Footer Content */}
        <div style={{ position: "relative", zIndex: 1 }}>
          <Container>
            {/* Top Section */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.3 }}
              transition={{ duration: 0.6 }}
              variants={fadeUp}
            >
              <Row className="align-items-center mb-5 mt-5">
                <Col md={8}>
                  <h2
                    className="fw-bold mb-3"
                    style={{ textAlign: "left", lineHeight: "1.4" }}
                  >
                    Learn to Grow with Intelligent Innovation
                  </h2>
                </Col>
                <Col md={4} className="text-md-end text-start mt-3 mt-md-0">
                  <Button
                    variant="light"
                    style={{
                      background: "#dd31ddff",
                      color: "#fff",
                      fontWeight: "600",
                      borderRadius: "30px",
                      padding: "10px 25px",
                    }}
                    onClick={() => navigate(`/contact`)}
                  >
                    Contact Us
                  </Button>
                </Col>
              </Row>
            </motion.div>

            <hr style={{ borderColor: "rgba(0,0,0,0.1)" }} />

            {/* Main Footer Columns */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.2 }}
              transition={{ duration: 0.7, staggerChildren: 0.2 }}
            >
              <Row className="mt-5 mb-5 text-start">
                {/* Column 1 - Logo & Description */}
                <motion.div variants={fadeUp} className="col-md-4 mb-4 pe-4"  >
                  <div className="logo mb-3">
                    <img
                      src={Logo}
                      alt="Foxtroit Logo"
                      style={{ height: "24px", objectFit: "contain" }}
                    />
                  </div>
                  <p style={{ opacity: 0.8, textAlign: "left", fontSize: "15px" }}>
                    At Foxtroit Private Limited, we empower industries with
                    cutting-edge AI solutions that transform how machines see,
                    think, and act. From logistics to healthcare — our
                    intelligent systems automate operations, improve safety,
                    and redefine efficiency.
                  </p>
                  <div className="d-flex gap-3 mt-3">
                    <a href="#" className="social-icon"><FaTwitter /></a>
                    <a href="#" className="social-icon"><FaFacebookF /></a>
                    <a href="#" className="social-icon"><FaInstagram /></a>
                    <a href="#" className="social-icon"><FaLinkedin /></a>
                  </div>
                </motion.div>

                {/* Column 2 - Company */}
                <motion.div variants={fadeUp} className="col-md-2 mb-4 ps-4">
                  <h6 className="fw-bold mb-3">Company</h6>
                  <ul className="list-unstyled" style={{ lineHeight: "2" }}>
                    <li><Link to="/" className="footer-link">Home</Link></li>
                    <li><Link to="/ai" className="footer-link">AI Agents</Link></li>
                    <li><Link to="/about" className="footer-link">About</Link></li>
                    <li>
                      <a
                        href="https://drive.google.com/file/d/1GDK9L6aePlarQKkCiWYW0b2QaDuAPpB_/view"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="footer-link"
                      >
                        Portfolio
                      </a>
                    </li>
                    <li><Link to="/contact" className="footer-link">Contact</Link></li>
                  </ul>
                </motion.div>

                {/* Column 3 - Help */}
                <motion.div variants={fadeUp} className="col-md-3 mb-4">
                  <h6 className="fw-bold mb-3">Help</h6>
                  <ul className="list-unstyled" style={{ lineHeight: "2" }}>
                    <li><Link to="/customer" className="footer-link">Customer Support</Link></li>
                    <li><Link to="/terms" className="footer-link">Terms & Conditions</Link></li>
                    <li><Link to="/privacy" className="footer-link">Privacy Policy</Link></li>
                  </ul>
                </motion.div>

                {/* Column 4 - Contact */}
                <motion.div variants={fadeUp} className="col-md-3 mb-4">
                  <h6 className="fw-bold mb-3">Contact</h6>
                  <ul className="list-unstyled" style={{ lineHeight: "2" }}>
                    <li><a href="tel:+919861632429" className="footer-link">+91 9861632429</a></li>
                    <li><a href="mailto:info@foxtroit.in" className="footer-link">info@foxtroit.in</a></li>
                    <li><span className="footer-link">MR Building, 3rd Floor</span></li>
                    <li><span className="footer-link">HBR Layout, Hennur Cross</span></li>
                    <li><span className="footer-link">Bangalore, Karnataka 560043, India</span></li>
                  </ul>
                </motion.div>
              </Row>

              {/* Inline Styling */}
              <style>
                {`
                  .footer-link {
                    color: rgba(0,0,0,0.8);
                    text-decoration: none;
                    transition: color 0.3s;
                  }
                  .footer-link:hover { color: #7c2ae8; }

                  .social-icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 40px;
                    height: 40px;
                    background: linear-gradient(145deg, #d4197a, #7c2ae8);
                    border-radius: 50%;
                    color: white;
                    font-size: 1.2rem;
                    transition: all 0.3s ease;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
                  }
                  .social-icon:hover {
                    transform: scale(1.2);
                  }
                `}
              </style>
            </motion.div>

            {/* Copyright */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              transition={{ duration: 0.5 }}
              variants={fadeUp}
            >
              <Row className="pt-4 mt-4 border-top border-secondary text-start">
                <Col style={{ opacity: 0.6 }}>
                  © {new Date().getFullYear()} Foxtroit | All Rights Reserved
                </Col>
              </Row>
            </motion.div>
          </Container>
        </div>
      </footer>
    </section>
  );
};

export default Footer;
